import Dispatcher from "./dispatcher";
import { QueryStore } from "./AbstractStores";

export function queryStore(store: QueryStore<any>) {
    return function (this: any, target: any, key: string, d: any) {
        const descriptor = action.apply(this, arguments as any);
        descriptor.value = cache(descriptor.value, store).bind(this);
        return descriptor;
    };
}

let typeCount = 100;
export function asAction<T, TFunction extends (...args: any[]) => Promise<T>>(originalFunction: TFunction) {
    const actionType = typeCount++;
    const result = waitPending(function (this: any) {
        var res = originalFunction(...Array.prototype.slice.call(arguments));
        return res.then(data => {
            Dispatcher.dispatch({
                actionType: actionType,
                data: data
            });
            return data;
        }).catch(data => {
            Dispatcher.dispatch({
                actionType: -actionType,
                data: data
            });
            return Promise.reject(data);
        });
    }) as TFunction;
    (result as any).actionType = actionType;
    return result;
}

export function action(this: any, target: any, key: string, descriptor: any) {
    const originalFunction = descriptor.value;
    originalFunction.actionType = typeCount++;
    descriptor.value = waitPending(function (this: any): any {
        var res = <Promise<any>>(originalFunction.apply(target, arguments));
        return res.then(data => {
            Dispatcher.dispatch({
                actionType: originalFunction.actionType,
                data: data
            });
            return data;
        }).catch(data => {
            Dispatcher.dispatch({
                actionType: -originalFunction.actionType,
                data: data
            });
            return Promise.reject(data);
        });
    }).bind(this);
    descriptor.value.actionType = originalFunction.actionType;
    return descriptor;
}
export function actionNoCatch(this: any, target: any, key: string, descriptor: any) {
    const originalFunction = descriptor.value;
    originalFunction.actionType = typeCount++;
    descriptor.value = waitPending(function (this: any): any {
        var res = <Promise<any>>(originalFunction.apply(target, arguments));
        return res.then(data => {
            Dispatcher.dispatch({
                actionType: originalFunction.actionType,
                data: data
            });
            return data;
        });
    }).bind(this);
    descriptor.value.actionType = originalFunction.actionType;
    return descriptor;
}

export default action;

export function actionMethod(this: any, target: any, key: string, descriptor: any) {
    var originalFunction = descriptor.value;
    descriptor.value = waitPending(function (this: any): any {
        var self = this;
        var res = <Promise<any>>(originalFunction.apply(target, arguments));
        return res.then(data => {
            Dispatcher.dispatch({
                actionType: self.type,
                data: data
            });
            return data;
        }).catch(data => {
            Dispatcher.dispatch({
                actionType: -originalFunction.actionType,
                data: data
            });
            return Promise.reject(data);
        });
    }).bind(this);
    return descriptor;
}

export function waitPending<TFunction extends Function>(f: TFunction): TFunction {
    var pending: { [key: string]: Promise<any> } = {};
    var result = function (this: any) {
        var key = JSON.stringify(arguments);
        var accumulated = pending[key];
        if (accumulated)
            return accumulated;
        pending[key] = f.apply(this, arguments);
        pending[key].then(_ => delete pending[key]).catch(_ => delete pending[key]);
        return pending[key];
    };
    return <any>result;
}

export function cache<TFunction extends Function>(f: TFunction, store: QueryStore<any>): TFunction {
    var result = function (this: any) {
        const args = Array.prototype.slice.call(arguments);
        var key = JSON.stringify(args);
        f.apply(this, arguments).then((x: any) => Array.isArray(x) ? store.setMany(x) : store.set(x));
        store.addQuery(key);
    };
    return <any>result;
}

export function bindActions<T>(actions: T, store: QueryStore<any>, options: { onXhr?: () => void, callback: () => void }) {
    store.Changed.register(options.callback);
    let obj: any = {};
    const functions = Object.getOwnPropertyNames(Object.getPrototypeOf(actions));
    for (let name of functions) {
        const f = (actions as any)[name];
        if (typeof f !== "function")
            continue;
        obj[name] = function (this: any) {
            const key = JSON.stringify(arguments);
            if (!store.hasQuery(key)) {
                if (options.onXhr) options.onXhr();
                f.apply(this, arguments);
                store.addQuery(key);
            }
            else {
                options.callback();
            }
        }.bind(actions);
    }
    obj.dispose = () => { store.Changed.unregister(options.callback) }
    return obj as (T & { dispose: () => void });
}